/**
 * RTK Query endpoints for the voice-service (@cluster/voice-service).
 * Base path after the dev proxy + nginx rewrite strips `/api`: `/voice`.
 * NOTE: the service returns DTOs with `id` (not `_id`).
 */
import { apiSlice } from './apiSlice';
import type {
  Voice,
  CreateVoiceDto,
  UpdateVoiceDto,
  VoiceTestResult,
  VoiceSource,
} from '@/types';

export interface SynthesizeDto {
  voiceId: string;
  text: string;
  conversationId?: string;
  pipelineId?: string;
  emitTo?: { kind: 'subject' | 'storage' | 'event'; target: string };
}

export interface SynthesizeResult {
  fileId: string;
  url?: string;
  bytes?: number;
  durationMs?: number;
  /** How the provider voice was resolved (`default` → cloned voice not used). */
  source?: VoiceSource;
  emitTo?: SynthesizeDto['emitTo'];
}

function voiceId(v: Voice): string {
  return v.id || v._id || '';
}

function data<T>(response: { data: T }): T {
  return response.data;
}

export const voiceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVoices: builder.query<Voice[], void>({
      query: () => '/voice/voices',
      transformResponse: data<Voice[]>,
      providesTags: (result) =>
        result
          ? [
              ...result.map((v) => ({ type: 'Voice' as const, id: voiceId(v) })),
              { type: 'Voice', id: 'LIST' },
            ]
          : [{ type: 'Voice', id: 'LIST' }],
    }),

    getVoice: builder.query<Voice, string>({
      query: (id) => `/voice/voices/${id}`,
      transformResponse: data<Voice>,
      providesTags: (_result, _error, id) => [{ type: 'Voice', id }],
    }),

    createVoice: builder.mutation<Voice, CreateVoiceDto>({
      query: (body) => ({ url: '/voice/voices', method: 'POST', body }),
      transformResponse: data<Voice>,
      invalidatesTags: [{ type: 'Voice', id: 'LIST' }],
    }),

    updateVoice: builder.mutation<Voice, { id: string; body: UpdateVoiceDto }>({
      query: ({ id, body }) => ({ url: `/voice/voices/${id}`, method: 'PATCH', body }),
      transformResponse: data<Voice>,
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'Voice', id }, { type: 'Voice', id: 'LIST' }]
          : [{ type: 'Voice', id: 'LIST' }],
    }),

    deleteVoice: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/voice/voices/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Voice', id: 'LIST' }],
    }),

    /** POST /voice/synthesize — TTS → storage file + optional emit. */
    synthesizeVoice: builder.mutation<SynthesizeResult, SynthesizeDto>({
      query: (body) => ({ url: '/voice/synthesize', method: 'POST', body }),
    }),

    /** POST /voice/voices/:id/test — short sample of the voice. */
    testVoice: builder.mutation<
      VoiceTestResult,
      { id: string; text: string; collectionId?: string }
    >({
      query: ({ id, text, collectionId }) => ({
        url: `/voice/voices/${id}/test`,
        method: 'POST',
        // `collectionId` overrides the service's VOICE_OUTPUT_COLLECTION_ID.
        body: { text, collectionId },
      }),
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetVoicesQuery,
  useGetVoiceQuery,
  useCreateVoiceMutation,
  useUpdateVoiceMutation,
  useDeleteVoiceMutation,
  useSynthesizeVoiceMutation,
  useTestVoiceMutation,
} = voiceApi;